import type { ChatNotice } from "../lib/chatNotice";

export enum ReferenceType {
  Document = "DOCUMENT",
  Sql = "SQL",
  Message = "MESSAGE",
  ExternalTicket = "EXTERNAL_TICKET",
  Memory = "MEMORY",
}

export interface AssistantToolCall {
  id: string;
  toolName: string;
  status: string;
  resultSummary?: string | null;
  executionDurationMs?: number | null;
  resultCount?: number | null;
}

export interface AssistantReference {
  id: string;
  index?: number | null;
  /**
   * Operator selection state for proposed-answer sources (page 1). Undefined
   * for assistant references, which have no selection concept.
   */
  selected?: boolean | null;
  type: string;
  title: string;
  excerpt?: string | null;
  externalLink?: string | null;
  sourceId?: string | null;
  sourceDocumentId?: string | null;
  sourceDocumentPageNumber?: number | null;
  sourceDatabaseTableId?: string | null;
  sourceDatabaseRowPkValues?: string | null;
  sourceDatabaseColumnName?: string | null;
  paragraphIndices?: (number | null)[] | null;
  quoteStart?: string | null;
  sourceConversationId?: string | null;
  sourceConversationMessageId?: string | null;
  sourceConversationExternalId?: string | null;
  /** MEMORY (Kundenhistorie) facts: contact context id for the /contacts/:id CRM link. */
  sourceContactContextId?: string | null;
  sourceMessageEmailMseMessageId?: string | null;
  /** Message.summary of the source message — used as the description for Message-type refs. */
  sourceMessageSummary?: string | null;
  /**
   * The customer to-do (rag_question text) this source was retrieved for, when it
   * could be attributed to one. Used on page 1 to group sources under each to-do.
   * Null/undefined sources render in a "Weitere Quellen" group.
   */
  customerRequestId?: string | null;
  /**
   * Machine scope (LiDO/Kramer/Gloria and any tenant with a scope hook): the type
   * the REQUEST was about. Set only when the retrieval scope engine judged this
   * source to be about a different machine type, so its presence IS the
   * "similar type" marker — a matched source, an unjudgeable one, and a tenant
   * without a scope hook all leave it null and render unmarked.
   */
  scopeExpectedLabel?: string | null;
  /**
   * The machine type this source is actually about ("PTS 2/SV"). Optional detail
   * next to `scopeExpectedLabel`: a source covering many types names none of them,
   * and the marker then falls back to generic wording.
   */
  scopeSourceLabel?: string | null;
  /**
   * The cross-cutting property this source gets wrong, worded for the operator
   * ("Steuerungsplattform"). Set independently of the two fields above: a source can
   * be about the right machine type and still run a different control platform, and
   * that mismatch is priced heavier than the type itself.
   */
  scopeFactLabel?: string | null;
  /**
   * ISO date the source's CONTENT is from — a document's authored/content date,
   * a past case's send date. Not when it was ingested. Shown quietly next to the
   * title so an operator can see at a glance that a card answering today's
   * request documents a situation from years ago; null for undated content, which
   * renders without a date rather than with an "unknown" label.
   */
  contentDate?: string | null;
  /**
   * A cited row of a table read as past cases, as the service case it is: the card
   * shows it as a case (number, date, text), never as the table it is stored in.
   */
  pastCase?: PastCaseView | null;
}

/** A cited past-case row as its case (the backend's `PastCase`). */
export interface PastCaseView {
  caseNumber: string;
  /** ISO date from the table's date column. */
  date?: string | null;
  text?: string | null;
}

/** Regex-classified identifier extracted from the user's message text.
 *
 * Produced by the backend scanner at message-creation time; rendered as
 * an inline chip on the user's chat bubble. `externalLink` is non-null
 * when the tenant pattern carries a `url_template`. */
export interface AssistantMessageIdentifier {
  id: string;
  /** The attribute kind of the pattern that found the value (ADR-0070). */
  attributeKey: string;
  value: string;
  label?: string | null;
  externalLink?: string | null;
}

/**
 * A file the support agent uploaded into the chat (PDF, image, or Word).
 *
 * The chat agent always receives these. `useInAnswer` is the separate, explicit
 * opt-in for feeding the file into answer generation for the drafted reply.
 */
export interface AssistantAttachment {
  id: string;
  name: string;
  contentType: string;
  /** Serialized as a string by the backend's Int64 scalar. */
  size: string;
  /** False while captioning / text extraction is still running. */
  isProcessed: boolean;
  /** True once extraction produced text. False with isProcessed means it came back empty. */
  hasExtractedText: boolean;
  useInAnswer: boolean;
  /** True when only the extracted text reaches the model, never the file itself (Word). */
  textOnly: boolean;
}

/** A staged upload still in the composer, with client-side progress state. */
export interface PendingChatAttachment {
  /** Server attachment id, or null while the add mutation is still in flight. */
  id: string | null;
  /** Local key, stable for the lifetime of the row (server id may arrive later). */
  key: string;
  name: string;
  contentType: string;
  size: number;
  /** 0-100. */
  progress: number;
  status: "uploading" | "ready" | "failed";
  error?: string;
}

export interface AssistantMessage {
  id: string;
  createdAt?: string | null;
  text: string;
  /** Written by a person: an operator, or a customer in a customer chat, who has no
   * `agent`. */
  isFromUser: boolean;
  agent?: { id: string; givenName?: string; familyName?: string } | null;
  toolCalls?: AssistantToolCall[];
  /**
   * Client-derived (progress messages only): tool calls that first appeared
   * AFTER answer text had already started streaming. Rendered below the text
   * so the live tool-call list follows temporal order — tools that ran before
   * the answer sit above it (`toolCalls`), tools triggered afterwards below.
   */
  toolCallsAfterText?: AssistantToolCall[];
  references?: AssistantReference[];
  /** The chat user's own rating of this answer: 1 helpful, -1 not helpful. */
  feedback?: number | null;
  /** Why the chat user rated the answer so. */
  feedbackComment?: string | null;
  choiceOptions?: string[] | null;
  /**
   * Proposed add_fact content(s) this message asks the operator to confirm
   * (empty unless this IS a pending add_fact confirmation). Lets the client
   * show the fact(s) the instant "Ja" is clicked, instead of waiting for the
   * confirming turn to write and broadcast them.
   */
  pendingFactContents?: string[];
  identifiers?: AssistantMessageIdentifier[];
  attachments?: AssistantAttachment[];
  /** Why an answer has no text of its own; shown through `messageText`. */
  notice?: ChatNotice | null;
}

/** Progress messages have no createdAt — they represent live tool-call status. */
export function isProgressMessage(msg: { createdAt?: string | null }): boolean {
  return !msg.createdAt;
}
